// src/hooks/menu/useMenuSpecials.js
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSpecialsList } from '../dutchie-queries';
import useFetchRest from '../useFetchRest';
import { consoleDebug } from '../../util';
import { 
    filterSpecialProductsByActiveSpecials,
    getMostRecentSpecialForProduct as getMostRecentUtil 
} from '../../utils/menu/specialsUtils';

// Promise-based data readiness tracker
const createDataReadinessPromise = () => {
    let resolvePromise;
    const promise = new Promise((resolve) => {
        resolvePromise = resolve;
    });
    return { promise, resolve: resolvePromise };
};

export const useMenuSpecials = (retailerId, showEmptySpecials) => {
    const [specialProductsIds, setSpecialProductsIds] = useState({});
    const [specialBrands, setSpecialBrands] = useState([]);
    const isInitialized = useRef(false);
    
    // Promise-based readiness tracking
    const dataReadinessRef = useRef(createDataReadinessPromise());
    const [isDataReady, setIsDataReady] = useState(false);
    
    // Fetch both data sources in parallel
    const { 
        data: menuAPIData, 
        isFetched: isFetchedMenuData,
        isError: isMenuError,
        error: menuError 
    } = useFetchRest(
        `data/menu?retailerId=${retailerId}`,
        {
            staleTime: 5 * 60 * 1000, // 5 minutes
            cacheTime: 30 * 60 * 1000, // 30 minutes
            enabled: !!retailerId, // Only fetch if retailerId exists
        }
    );
    
    const { 
        data: apiSpecialsList, 
        isFetched: isFetchedSpecials,
        isError: isSpecialsError,
        error: specialsError
    } = useSpecialsList(retailerId);

    // Initialize data immediately when available
    useEffect(() => {
        if (menuAPIData && !isInitialized.current) {
            consoleDebug('🎯 Menu data loaded, initializing specials', {
                specialProductsIds: Object.keys(menuAPIData.specialProductsIds || {}).length,
                specialBrands: menuAPIData.specialBrands
            });
            
            // Handle both object and array formats for specialProductsIds
            const productIds = Array.isArray(menuAPIData.specialProductsIds) 
                ? {} 
                : (menuAPIData.specialProductsIds || {});
            
            // Handle both boolean and array formats for specialBrands
            const brands = menuAPIData.specialBrands === false 
                ? [] 
                : (Array.isArray(menuAPIData.specialBrands) ? menuAPIData.specialBrands : []);
            
            setSpecialProductsIds(productIds);
            setSpecialBrands(brands);
            isInitialized.current = true;
        }
    }, [menuAPIData]);

    // Handle errors
    useEffect(() => {
        if (isMenuError) {
            consoleDebug('❌ Menu API error', menuError);
            // Set default values on error
            setSpecialProductsIds({});
            setSpecialBrands([]);
            isInitialized.current = true;
        }
    }, [isMenuError, menuError]);

    // Check if specials exist
    const specialsExist = useMemo(() => 
        Array.isArray(apiSpecialsList) && apiSpecialsList.length > 0,
        [apiSpecialsList]
    );

    // Get selected special - memoized for performance
    const getSelectedSpecial = useCallback((selectedSpecialId) => {
        if (!specialsExist || !selectedSpecialId || !apiSpecialsList) return null;
        return apiSpecialsList.find(special => special.id === selectedSpecialId) || null;
    }, [specialsExist, apiSpecialsList]);

    // Get filtered special product IDs - memoized to prevent recalculation
    const filteredSpecialProductsIds = useMemo(() => {
        if (!apiSpecialsList || !specialProductsIds || Object.keys(specialProductsIds).length === 0) {
            return {};
        }
        
        const filtered = filterSpecialProductsByActiveSpecials(
            specialProductsIds, 
            apiSpecialsList, 
            showEmptySpecials
        );
        
        consoleDebug('📊 Filtered special products', {
            original: Object.keys(specialProductsIds).length,
            filtered: Object.keys(filtered).length
        });
        
        return filtered;
    }, [apiSpecialsList, specialProductsIds, showEmptySpecials]);

    // Get most recent special ID for a product - optimized with memoization
    const getMostRecentSpecialId = useCallback((productId) => {
        if (!productId || !filteredSpecialProductsIds || Object.keys(filteredSpecialProductsIds).length === 0) {
            return null;
        }
        return getMostRecentUtil(productId, filteredSpecialProductsIds, apiSpecialsList);
    }, [apiSpecialsList, filteredSpecialProductsIds]);

    // Create a product-to-special mapping for O(1) lookup
    const productSpecialMap = useMemo(() => {
        const map = new Map();
        
        if (filteredSpecialProductsIds && apiSpecialsList) {
            Object.entries(filteredSpecialProductsIds).forEach(([specialId, productIds]) => {
                const special = apiSpecialsList.find(s => s.id === specialId);
                if (special && productIds) {
                    productIds.forEach(productId => {
                        if (!map.has(productId)) {
                            map.set(productId, []);
                        }
                        map.get(productId).push(special);
                    });
                }
            });
        }
        
        return map;
    }, [filteredSpecialProductsIds, apiSpecialsList]);

    // Optimized function to get specials for a product
    const getSpecialsForProduct = useCallback((productId) => {
        return productSpecialMap.get(productId) || [];
    }, [productSpecialMap]);

    // Promise-based data readiness coordination
    useEffect(() => {
        const bothDataSourcesReady = (isFetchedMenuData || isMenuError) && isFetchedSpecials;
        
        if (bothDataSourcesReady && !isDataReady) {
            consoleDebug('🎯 PROMISE: Both specials data sources ready, resolving promise', {
                isFetchedMenuData,
                isFetchedSpecials,
                hasSpecialsList: !!apiSpecialsList,
                specialsCount: apiSpecialsList?.length || 0,
                hasSpecialProductsIds: Object.keys(specialProductsIds).length > 0
            });
            
            setIsDataReady(true);
            dataReadinessRef.current.resolve({
                specialsData: apiSpecialsList,
                productsData: specialProductsIds,
                timestamp: Date.now()
            });
        }
    }, [isFetchedMenuData, isFetchedSpecials, isMenuError, isDataReady, apiSpecialsList, specialProductsIds]);

    // Create a promise that components can await
    const waitForSpecialsData = useCallback(() => {
        if (isDataReady) {
            return Promise.resolve({
                specialsData: apiSpecialsList,
                productsData: specialProductsIds,
                timestamp: Date.now()
            });
        }
        return dataReadinessRef.current.promise;
    }, [isDataReady, apiSpecialsList, specialProductsIds]);

    return {
        specialProductsIds,
        specialBrands,
        apiSpecialsList,
        specialsExist,
        filteredSpecialsList: apiSpecialsList || [],
        filteredSpecialProductsIds,
        getSelectedSpecial,
        getMostRecentSpecialId,
        getSpecialsForProduct,
        isFetchedMenuData: (isFetchedMenuData || isMenuError) && isFetchedSpecials,
        // Promise-based data readiness
        isDataReady,
        waitForSpecialsData,
        productSpecialMap,
        isError: isMenuError || isSpecialsError,
        error: menuError || specialsError
    };
};
